import { useForm } from 'react-hook-form';
import { Alert, Box, Text, Tooltip } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import { Button, Input, PasswordStrength } from 'components';
import { passwordRegex, yup } from 'utils';

type PayloadType = { password: string };
/* eslint-disable no-unused-vars */
type FormType = {
  onButton: (data: PayloadType) => void;
  csrfLoading: boolean;
  isSubmitting: boolean;
};
const Form: React.FC<FormType> = ({
  onButton,
  csrfLoading,
  isSubmitting,
}: FormType) => {
  const { t } = useTranslation('component.auth.password.form');
  const schema = yup.object().shape({
    password: yup.string().label('password').matches(passwordRegex).required(),
    confirm_password: yup
      .string()
      .label('confirm_password')
      .oneOf([yup.ref('password')], t('same_password'))
      .required(),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      confirm_password: '',
    },
  });

  const onSubmit = async (data: PayloadType & { confirm_password: string }) => {
    onButton({ password: data.password });
  };

  const formProps = useInputAttributes(
    register,
    errors,
    {
      password: t('password'),
      confirm_password: t('confirm_password'),
    },
    ['password']
  );

  const formLocked = csrfLoading || isSubmitting;

  const fieldSurface = {
    background: 'secondary',
    borderWidth: '1px',
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 'lg',
    _hover: { background: 'rgba(255,255,255,0.05)' },
    _focus: {
      background: 'secondary',
      borderColor: 'primary',
    },
  };

  return (
    <Box w="100%">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={5}>
          <Input
            type="password"
            {...formProps.password}
            {...fieldSurface}
            disabled={formLocked}
          />
        </Box>
        <Box mb={5}>
          <Input
            type="password"
            {...formProps.confirm_password}
            {...fieldSurface}
            disabled={formLocked}
          />
        </Box>
        <PasswordStrength password={watch('password')} />
        <Alert.Root
          status="warning"
          mb={5}
          borderRadius="md"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.12)"
        >
          <Tooltip.Root openDelay={200} positioning={{ placement: 'top' }}>
            <Tooltip.Trigger>
              <Box as="span" display="inline-flex" cursor="default">
                <Alert.Indicator />
              </Box>
            </Tooltip.Trigger>
            <Tooltip.Positioner>
              <Tooltip.Content>{t('passwordTipHint')}</Tooltip.Content>
            </Tooltip.Positioner>
          </Tooltip.Root>
          <Text fontSize="sm" color="warning">
            {t('passwordTip')}
          </Text>
        </Alert.Root>
        <Button
          type="submit"
          variant="filled"
          display="block"
          title={t('submit')}
          minW="100%"
          mt={8}
          isLoading={isSubmitting}
          isDisabled={csrfLoading || isSubmitting}
        />
      </form>
    </Box>
  );
};

export default Form;
